import React from 'react';
import {
    Grid,
    TextField,
    Typography,
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    Button,
    MenuItem,
} from '@mui/material';
import FieldGroup from '../FieldGroup';
import { DropzoneUpload } from 'routes/Pages/PurchaseRequisition/components/FormControls';
import { followUpSectionDropDownData } from '../../data/EnquiryFormIpFields';

// Section title with optional inline controls on the right
function SectionTitle({ children, right }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 1.5,
                flexWrap: 'wrap',
            }}
        >
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#4a3f2f' }}>
                {children}
            </Typography>
            {right}
        </Box>
    );
}

const cardSx = {
    border: '1px solid #ece7df',
    borderRadius: '10px',
    p: 2.5,
    backgroundColor: '#fff',
};

export default function EnquiryDetailsStep({ form, deleteContactDoc, downloadContactDoc }) {
    const {
        state,
        setField,
        mode,
        existingDocs,
        uploadedDocs,
        setUploadedDocs,
        selectedCrmId,
        followUpSectionIpFields,
        officeUseSectionIpFields,
        showOfficeUse,
        showBrochure 
    } = form;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ── Follow-up ─────────────────────────────────────────── */}
            <FieldGroup title="Follow-up">
                <Grid item xs={12} sm={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        label="Follow-up Date"
                        InputLabelProps={{ shrink: true }}
                        value={state.followUpDate ?? ''}
                        onChange={e => setField('followUpDate', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        size="small"
                        fullWidth
                        select
                        label="Enquiry Rating"
                        value={state.enquiryRating ?? ''}
                        onChange={e => setField('enquiryRating', e.target.value)}
                    >
                        {(followUpSectionDropDownData?.enquiryRating || []).map(opt => (
                            <MenuItem key={opt.value ?? opt} value={opt.value ?? opt}>
                                {opt.label ?? opt}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </FieldGroup>

            {/* ── Brochure ──────────────────────────────────────────── */}
            { showBrochure && <Box sx={cardSx}>
                <SectionTitle
                    right={
                        <RadioGroup
                            row
                            value={state.sendBrochure === true ? 'yes' : 'no'}
                            onChange={e => setField('sendBrochure', e.target.value === 'yes')}
                            sx={{ gap: 2 }}
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio />}
                                label="Yes"
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio />}
                                label="No, don't send"
                            />
                        </RadioGroup>
                    }
                >
                    Send a brochure?
                </SectionTitle>

                <Grid container>
                    {state.sendBrochure && (
                        <Grid item xs={12} sx={{ mb: 1 }}>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                label="Note to include with the brochure"
                                value={state.brochureNote}
                                onChange={e => setField('brochureNote', e.target.value)}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            label="Additional information shared with grower"
                            value={state.additionalInfoFromGrower}
                            onChange={e => setField('additionalInfoFromGrower', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Box>}

            {/* ── Attachments ───────────────────────────────────────── */}
            <FieldGroup title="Attachments">
                {mode === 'existing' && existingDocs?.length > 0 && (
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
                            Previously uploaded files
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {existingDocs.map(doc => (
                                <Box
                                    key={doc.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontSize: '0.875rem',
                                        p: 1,
                                        borderRadius: '6px',
                                        backgroundColor: '#faf8f5',
                                        minWidth: 0,        // ✅ allow inner content to shrink
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '0.875rem',
                                            flex: 1,
                                            minWidth: 0,      // ✅ filename can ellipsize
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {doc.documentName}
                                    </Typography>
                                    <Button size="small" onClick={() => downloadContactDoc?.(doc)}>
                                        View
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => deleteContactDoc?.(selectedCrmId, doc)}
                                    >
                                        Delete
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                )}

                {/* ✅ This wrapper is the fix — scopes overflow without touching DropzoneUpload */}
                <Grid item xs={12} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <DropzoneUpload
                        label="Upload Documents"
                        multiple
                        uploadedFiles={uploadedDocs}
                        onFileSelect={files => setUploadedDocs(files)}
                        onFileDelete={file => setUploadedDocs(prev => prev.filter(f => f !== file))}
                        onFileView={() => { }}
                    />
                </Grid>
            </FieldGroup>

            {/* ── Internal notes — multi-line textarea ─────────────── */}
            <FieldGroup title="Internal notes">
                <Grid item xs={12}>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={10}
                        placeholder="Add any internal notes about this enquiry…"
                        value={state.followUpDetails ?? ''}
                        onChange={e => setField('followUpDetails', e.target.value)}
                    />
                </Grid>
            </FieldGroup>

            {/* ── Office use only ───────────────────────────────────── */}
            {showOfficeUse && (
                <FieldGroup title="Office use only">
                    {officeUseSectionIpFields.map((f, i) => (
                        <Grid item xs={12} sm={6} md={4} key={`${f.name}_${i}`}>
                            <TextField
                                size="small"
                                fullWidth
                                type={f.textFieldType || 'text'}
                                label={f.label}
                                value={state[f.name] ?? ''}
                                onChange={e => setField(f.name, e.target.value)}
                            />
                        </Grid>
                    ))}
                </FieldGroup>
            )}
        </Box>
    );
}